import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useWidget } from '../../providers/widget'
import { useFetchWidget } from '../../hooks'
import { useLocaleDetection } from '../../hooks/locale'
import { useLocalizedTranslation } from '../i18n'
import type { ExploreProperty } from '../../widgets/explorer/data'
import { currencyPrefix } from './currency-prefix'
import { CompareMark, type CardCompare } from './card-compare'
import { isFavorite, toggleFavorite } from './favorites'
import { feedNoDatesLabelForProperty } from './no-dates-label'
import { propertyLinkRel, resolvePropertyLink } from '../property-href'
import { usePropertyHrefFeed } from '../use-property-href-feed'
import { CardTierBadge } from './TierBadge'
import type { TierRatingConfig } from '../../widgets/explorer/tier-rating/schema'
import {
  alignClass,
  resolveCardElements,
  type CardElement,
  type CardElementsByTemplate,
  type CardElementTemplate,
} from './card-elements'
import { resolveCardRating, type CardRatingPlacement } from './card-rating'
import { readCardTagsConfig, collectCardTags, cardTagPillStyle } from './label-style'
import type { CardTagsConfig } from '../../widgets/explorer/labels/schema'

/**
 * Translation keys the card surfaces read. Mirrors propertyCardTranslationsSchema
 * in widgets/property-card/config.ts — the schema provides defaults, this type is
 * the structural contract so the shared module has no upward config import.
 */
export interface SharedCardTranslations {
  price_from: Record<string, string>
  price_per_night: Record<string, string>
  view_property: Record<string, string>
  photo_previous: Record<string, string>
  photo_next: Record<string, string>
  photo_position: Record<string, string>
  feature_guest: Record<string, string>
  feature_guests: Record<string, string>
  feature_bedroom: Record<string, string>
  feature_bedrooms: Record<string, string>
  feature_bedrooms_short: Record<string, string>
  feature_studio: Record<string, string>
  feature_bath: Record<string, string>
  feature_baths: Record<string, string>
  feature_baths_short: Record<string, string>
  rating_reviews: Record<string, string>
}

/**
 * Structural contract the template components read. PropertyCardWidgetConfig
 * satisfies it; any future consumer (e.g. explorer offering these templates)
 * feeds a mapped object with the same shape.
 */
export interface SharedCardOptions {
  showImage: boolean
  showPrice: boolean
  showLocation: boolean
  showFeatures: boolean
  showPropertyType: boolean
  showExcerpt: boolean
  imageAspectRatio: string
  maxImages: number
  overlayGradientStrength: 'soft' | 'strong'
  openInNewTab: boolean
  language: { useAutoDetectedLocale: boolean; locale: string }
  translations: SharedCardTranslations
  /**
   * #825: per-template element order + alignment. Optional on purpose: the
   * explorer bridge passes nothing, so the grid renders each template's
   * designed order (docs/property-card-elements.md).
   */
  elements?: CardElementsByTemplate
  /**
   * #1813: the explorer's Tier Rating ladder. Optional on purpose: the
   * standalone Property Card widget passes nothing, so it renders no badge.
   */
  tierRating?: TierRatingConfig | null
  /**
   * Card rating threshold (toolkit card-rating `min_reviews`): the explorer
   * bridge passes `card.ratingMinReviews`. Optional on purpose: unset (and the
   * standalone Property Card widget) keeps the old gate, any count above 0.
   */
  ratingMinReviews?: number
  /**
   * Card rating placement (toolkit card-rating `placement`): the explorer
   * bridge passes `card.ratingPlacement` only when explicitly set. Unset keeps
   * the rating where the per-template element order puts it (byte-identical).
   */
  ratingPlacement?: CardRatingPlacement
  /**
   * Card tags (#1812): the explorer bridge passes its `card.cardTags` group so
   * the type chip can show the home's priority-ordered tag pills instead.
   * Optional on purpose: the standalone property-card widget has no such
   * group, so its chip is untouched.
   */
  cardTags?: CardTagsConfig | null
}

export interface TemplateProps {
  property: ExploreProperty
  options: SharedCardOptions
}

/**
 * Grid mount context (explorer grid/slider/recommendations). The bridge
 * provides it so the templates carry the grid's live behavior — the
 * filter-carrying property URL, the explorer favorites heart, and quote-aware
 * pricing — without the standalone property-card widget knowing any of it.
 * Null (the default) means a standalone mount: static URL, no heart, PriceRow.
 */
export interface GridCardContext {
  /** Property URL carrying the visitor's dates/party filters. */
  href: string
  /** `href` leaves HomeRunner for the property's own page (External Property Pages, #1714). */
  external?: boolean
  /**
   * Unit Grouping (#1070, central#3210): call at CLICK TIME to re-resolve
   * `href` to a uniformly random sibling. A no-op returning the current
   * `href` unchanged when the property has no unit_group.
   */
  pickHref: () => string
  /** Render the explorer favorites heart on the card. */
  showFavorite: boolean
  /** Fully-rendered price block honoring the explorer priceDisplay modes
   *  (quotes, skeletons, per-stay fallbacks); null renders no price. */
  price: React.ReactNode | null
  /**
   * Compare Homes (#1713): present only while compare mode is on. The card
   * link steps out of the tab order (the select chip is the control) and the
   * chip mounts beside it; absent, the card renders exactly as before.
   */
  compare?: CardCompare | null
}

export const GridCardCtx = createContext<GridCardContext | null>(null)

export type CardTranslate = (key: keyof SharedCardTranslations, defaultValue?: string) => string

/**
 * #824: the card's `_t`, resolved against the widget's language settings.
 * Locale detection seeds with the configured locale (deterministic for SSR)
 * and switches to the browser locale after mount when auto-detect is on.
 * #1262: resolution is operator value → shipped locale table (the shared
 * card table, whichever widget hosts the card; configured locale only) →
 * en_US schema default.
 */
export function useCardTranslation(options: SharedCardOptions): { _t: CardTranslate } {
  const { detectedLocale } = useLocaleDetection(
    options.language.useAutoDetectedLocale,
    options.language.locale,
  )
  return useLocalizedTranslation('property-card', options.translations, detectedLocale, options.language)
}

/**
 * The price text every card surface prints.
 *
 * The LOCALE stays pinned to en-US: SSR (server locale) and hydration (browser
 * locale) must produce identical text or React flags a mismatch on
 * international clients. That is a constraint on the locale, though, and it had
 * been satisfied by hardcoding the SYMBOL too — so a GBP property read "$320"
 * on its card while the pin, the calendar and checkout on the same page read
 * "£320" (#1489). The currency CODE is data, identical on the server and in
 * the browser, so it can vary without touching the hydration guarantee.
 *
 * The SYMBOL is a different matter, and it is why this does not go through
 * Intl's currency formatting the way the pins and the calendar do: each runtime
 * looks symbols up in its own copy of the currency data, and the server's and a
 * visitor's need not agree (currency-prefix.ts has the measurement). The prefix
 * comes from a table that ships with the bundle; the only thing asked of the
 * runtime is en-US digit grouping, which is what the previous version asked.
 *
 * For USD this is the previous expression to the letter, so no USD feed, cached
 * SSR page or snapshot moves. Anything that is not three letters reads as USD.
 */
export function formatPrice(amount: number, currency?: string | null): string {
  // ASCII-only by the test above, so toUpperCase() cannot be locale-sensitive.
  const code = typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency) ? currency.toUpperCase() : 'USD'
  return `${currencyPrefix(code)}${Math.round(amount).toLocaleString('en-US')}`
}

/**
 * The nightly rate the card advertises, or null when there is nothing honest to
 * show. `lowestPrice` is the calendar-derived forward-window low; it falls back
 * to the base rate when Central has no calendar data for the property.
 */
export function nightlyPrice(property: ExploreProperty): number | null {
  const price = property.lowestPrice ?? property.price
  return price > 0 ? price : null
}

/**
 * The property page link: the feed's configured PDP route, or the property's
 * own page when the feed maps one (External Property Pages, #1714).
 */
export function usePropertyLink(property: ExploreProperty): { href: string; external: boolean } {
  const { feedId, widgetId } = useWidget()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  const pdpRoute = widgetData?.feed?.additional_info?.reverseProxy?.routes?.pdp || '/property/[slug]'
  const hrefFeed = usePropertyHrefFeed(widgetData?.feed)
  if (!property.slug) return { href: '#', external: false }
  const { href, external } = resolvePropertyLink({ feed: hrefFeed, slug: property.slug, pdpRoute })
  return { href, external }
}

/** The property page URL, using the feed's configured PDP route. */
export function usePropertyUrl(property: ExploreProperty): string {
  return usePropertyLink(property).href
}

/**
 * #1188: the "No dates available" label for surfaces without their own
 * `priceDisplay` (saved panel, standalone PriceRow), read from the feed-global
 * explorer settings via the widget payload the tree already fetches — no new
 * request. Null = show the surface's normal price (bookable rate, unvouched
 * calendar, or operator opt-out).
 */
export function useFeedNoDatesLabel(
  property: Pick<ExploreProperty, 'lowestPrice' | 'bookableNights' | 'calendarStale'>,
): string | null {
  const { feedId, widgetId } = useWidget()
  const { data: widgetData } = useFetchWidget(feedId, widgetId)
  return feedNoDatesLabelForProperty(widgetData?.feed?.additional_info?.explorer, property)
}

/**
 * Guests / bedrooms / baths as display strings, dropping any the property has no
 * positive number for. `abbreviated` is the overlay's single-line form, where the
 * meta shares a row with the location.
 */
export function featureParts(property: ExploreProperty, abbreviated: boolean, _t: CardTranslate): string[] {
  const { guests, beds, baths } = property.features
  const parts: string[] = []
  if (guests > 0) parts.push(`${guests} ${guests === 1 && !abbreviated ? _t('feature_guest', 'guest') : _t('feature_guests', 'guests')}`)
  if (beds > 0) parts.push(`${beds} ${abbreviated ? _t('feature_bedrooms_short', 'bd') : beds === 1 ? _t('feature_bedroom', 'bedroom') : _t('feature_bedrooms', 'bedrooms')}`)
  else parts.push(_t('feature_studio', 'Studio'))
  if (baths > 0) parts.push(`${baths} ${abbreviated ? _t('feature_baths_short', 'ba') : baths === 1 ? _t('feature_bath', 'bath') : _t('feature_baths', 'baths')}`)
  // Unit Grouping (#1070): minimal secondary-meta indicator, appended to the
  // same feature-chip row MetaRow already renders (and the overlay template's
  // joined meta line) — no new layout. Only a group's representative row
  // carries unit_group.
  if (property.unit_group && property.unit_group.size > 0) {
    parts.push(`${property.unit_group.size} units`)
  }
  return parts
}

export function locationLabel(property: ExploreProperty): string {
  return property.location?.label || (property.locations ?? []).map(l => l.name).join(' / ')
}

/**
 * The property-type chip over the photo (or at the top of the body when
 * there is no photo). Card tags (#1812): with the group on, the home's
 * listed tags take the chip's place as a wrap of pills in the operator's
 * priority order; a home with none of them keeps the type chip unless
 * keepType is off. Same `.hr-pc-chip` class on each pill so the chip's
 * position / z-index / pointer-events rules keep applying; the modifier and
 * the inline style carry the toolkit look. Unconfigured = today's chip.
 */
export function TypeChip({ property, options }: TemplateProps) {
  if (!options.showPropertyType) return null
  const cfg = readCardTagsConfig(options)
  const tags = cfg ? collectCardTags(property, cfg) : []
  if (tags.length > 0) {
    if (cfg!.pills === false) {
      // Text mode: one plain chip, the type chip's own look (the toolkit text
      // mode draws no pill border either).
      return <span className="hr-pc-chip">{tags.map((t) => t.text).join(' / ')}</span>
    }
    const style = cardTagPillStyle(cfg!)
    return (
      <span className="hr-pc-chips hr-pc-chips--tags">
        {tags.map((t) => <span key={t.key} className="hr-pc-chip hr-pc-chip--tag" style={style}>{t.text}</span>)}
      </span>
    )
  }
  if (cfg && cfg.keepType === false) return null
  if (!property.propertyType) return null
  return <span className="hr-pc-chip">{property.propertyType}</span>
}

export function PriceRow({ property, options, className = '' }: TemplateProps & { className?: string }) {
  const grid = useContext(GridCardCtx)
  const { _t } = useCardTranslation(options)
  const noDates = useFeedNoDatesLabel(property)
  // Grid mount: the bridge pre-renders the price with the explorer's full
  // display logic (quotes, loading skeleton, per-stay fallbacks).
  if (grid) return <>{grid.price}</>
  if (!options.showPrice) return null
  if (noDates !== null) return <p className={`hr-pc-price hr-pc-price--unavailable${className}`}>{noDates}</p>
  const price = nightlyPrice(property)
  if (price === null) return null
  return (
    <p className={`hr-pc-price${className}`}>
      {_t('price_from', 'From')} <b>{formatPrice(price, property.currency)}</b> <span className="hr-pc-price__unit">{_t('price_per_night', '/ night')}</span>
    </p>
  )
}

export function Rating({ property, options }: TemplateProps) {
  const { _t } = useCardTranslation(options)
  const resolved = ratingFor(property, options)
  if (!resolved) return null
  const rating = property.rating!
  return (
    <span
      className="hr-pc-rating"
      title={`${rating.count} ${_t('rating_reviews', 'reviews')}`}
      {...(resolved.ariaLabel ? { role: 'img', 'aria-label': resolved.ariaLabel } : {})}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" /></svg>
      <span className="hr-pc-rating__avg">{rating.average.toFixed(1)}</span>
      <span className="hr-pc-rating__count">({rating.count})</span>
    </span>
  )
}

export function MetaRow({ property, options, className = '' }: TemplateProps & { className?: string }) {
  const { _t } = useCardTranslation(options)
  if (!options.showFeatures) return null
  const parts = featureParts(property, false, _t)
  if (parts.length === 0) return null
  return (
    <div className={`hr-pc-meta${className}`}>
      {parts.map(part => <span key={part}>{part}</span>)}
    </div>
  )
}

/** Whether Rating renders anything for this property (mirrors its null-return). */
export function hasRating(property: ExploreProperty, options?: SharedCardOptions): boolean {
  return ratingFor(property, options) !== null
}

/**
 * The shared templates keep their element-order placement; only the review
 * threshold (and its announced line) comes from card-rating.ts. Unset
 * threshold = the old gate (any count above 0), so saved widgets are unchanged.
 */
function ratingFor(property: ExploreProperty, options?: SharedCardOptions) {
  return resolveCardRating(property.rating, {
    showRating: true,
    ratingMinReviews: options?.ratingMinReviews,
    ratingPlacement: options?.ratingPlacement,
  })
}

/**
 * Explicit placement only: 'title' puts the rating right after the title (the
 * shared heading row), 'location' right after the location line. Unset returns
 * the list untouched, so an unconfigured widget renders the same markup.
 */
function placeRating(elements: CardElement[], placement: CardRatingPlacement | undefined): CardElement[] {
  if (placement !== 'title' && placement !== 'location') return elements
  const at = elements.findIndex((el) => el.id === 'rating')
  if (at < 0) return elements
  const rest = elements.filter((_, i) => i !== at)
  const anchor = rest.findIndex((el) => el.id === placement)
  if (anchor < 0) return elements
  return [...rest.slice(0, anchor + 1), elements[at], ...rest.slice(anchor + 1)]
}

/**
 * #825: a template's body elements in the operator's order
 * (docs/property-card-elements.md). Reads `options.elements[template]`, or
 * the template's designed order when nothing is stored, and applies the
 * grouping rules: title + rating adjacent share the heading row; on Photo
 * Overlay, location + meta adjacent share one line in the order they sit;
 * the Detailed price keeps its view link. `default` alignment adds no class.
 * Every node is a direct child of the caller's body so the explorer's
 * card-skin-css selectors (`.hr-pc-body > …`) keep matching.
 */
export function CardElements({ property, options, template }: TemplateProps & { template: CardElementTemplate }) {
  const { _t } = useCardTranslation(options)
  const elements = template === 'overlay'
    ? resolveCardElements(options.elements, template)
    : placeRating(resolveCardElements(options.elements, template), options.ratingPlacement)
  const overlay = template === 'overlay'
  const location = options.showLocation ? locationLabel(property) : ''
  const excerpt = template === 'detailed' && options.showExcerpt ? (property.subtitle?.post_excerpt || '') : ''
  const nodes: React.ReactNode[] = []

  for (let i = 0; i < elements.length; i++) {
    const { id, align } = elements[i]
    const next = elements[i + 1]?.id
    const cls = alignClass(align)
    switch (id) {
      case 'title': {
        if (overlay) {
          nodes.push(<p key="title" className={`hr-pc-title${cls}`}>{property.title}</p>)
          break
        }
        const withRating = next === 'rating'
        nodes.push(
          <div key="title" className={`hr-pc-heading${cls}`}>
            <h3 className="hr-pc-title">{property.title}</h3>
            {withRating ? <Rating property={property} options={options} /> : null}
          </div>,
        )
        // Tier Rating (#1813), title placement: under the title, the toolkit's
        // spot. Null config = no node, so the body stays byte-identical.
        if (options.tierRating) {
          nodes.push(<CardTierBadge key="tier" property={property} config={options.tierRating} at="title" hasMedia={hasMedia(property, options)} />)
        }
        if (withRating) i++
        break
      }
      case 'rating': {
        // Only reached when moved away from the title; Overlay has no rating.
        if (!overlay && hasRating(property, options)) {
          nodes.push(
            <div key="rating" className={`hr-pc-ratingrow${cls}`}>
              <Rating property={property} options={options} />
            </div>,
          )
        }
        break
      }
      case 'location':
      case 'meta': {
        if (overlay) {
          const paired = (id === 'location' && next === 'meta') || (id === 'meta' && next === 'location')
          const text = (paired ? [id, next] : [id])
            .flatMap((part) => (part === 'location' ? [location] : options.showFeatures ? featureParts(property, true, _t) : []))
            .filter(Boolean)
            .join(' · ')
          if (text) nodes.push(<p key={id} className={`hr-pc-meta${cls}`}>{text}</p>)
          if (paired) i++
        } else if (id === 'location') {
          if (location) nodes.push(<p key="location" className={`hr-pc-location${cls}`}>{location}</p>)
        } else {
          nodes.push(<MetaRow key="meta" property={property} options={options} className={cls} />)
        }
        break
      }
      case 'excerpt': {
        if (excerpt) nodes.push(<p key="excerpt" className={`hr-pc-excerpt${cls}`}>{excerpt}</p>)
        break
      }
      case 'price': {
        if (template === 'detailed') {
          nodes.push(
            <div key="price" className={`hr-pc-pricerow${cls}`}>
              <PriceRow property={property} options={options} />
              <span className="hr-pc-viewlink">{_t('view_property', 'View property')}</span>
            </div>,
          )
        } else {
          nodes.push(<PriceRow key="price" property={property} options={options} className={cls} />)
        }
        break
      }
    }
  }
  return <>{nodes}</>
}

/** The favorites heart glyph, shared by every heart button (card + saved-panel). */
export function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  )
}

/** Anchor attributes shared by every template — the whole card is one link. */
export function linkProps(options: SharedCardOptions, external = false) {
  return {
    target: options.openInNewTab ? '_blank' : undefined,
    rel: propertyLinkRel({ external }, options.openInNewTab),
  }
}

/**
 * Stretched link covering the whole card (its ::after fills the card shell).
 * The slideshow buttons stack above it via z-index, so the card stays one big
 * click target without nesting interactive elements inside an anchor.
 *
 * In a grid mount it also carries the favorites heart: CardLink is a direct
 * child of every template's card shell, so rendering the heart as the
 * anchor's sibling puts it inside the shell without the templates knowing.
 */
export function CardLink({ property, options }: TemplateProps) {
  const grid = useContext(GridCardCtx)
  const staticLink = usePropertyLink(property)
  const href = grid?.href ?? staticLink.href
  const external = grid ? grid.external === true : staticLink.external
  // Unit Grouping (#1070): re-resolve to a fresh random sibling on every
  // click. grid.pickHref() already returns `href` unchanged when the
  // property has no unit_group, so this is unconditionally safe to call —
  // no separate branch for the ungrouped/standalone-mount case.
  const handleClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (grid?.pickHref) {
      e.currentTarget.href = grid.pickHref()
    }
  }, [grid])
  const compare = grid?.compare ?? null
  return (
    <>
      <a
        className="hr-pc-card__link"
        href={href}
        onClick={handleClick}
        {...linkProps(options, external)}
        {...(compare ? { tabIndex: -1, 'aria-hidden': true as const } : {})}
      >
        <span className="hr-pc-sr">{property.title}</span>
      </a>
      {grid?.showFavorite ? <CardFavoriteButton property={property} /> : null}
      {compare ? <CompareMark compare={compare} /> : null}
    </>
  )
}

/**
 * Explorer favorites heart on a template card. Same store and SSR-safe
 * seeding as the legacy card: state starts false (matches SSR, where
 * localStorage is unavailable) and syncs on mount (#771). Top-LEFT corner —
 * the type chip owns the top-right on these templates.
 */
export function CardFavoriteButton({ property }: { property: ExploreProperty }) {
  const [favorited, setFavorited] = useState(false)
  useEffect(() => {
    setFavorited(isFavorite(property.id))
  }, [property.id])
  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setFavorited(toggleFavorite(property.id))
  }, [property.id])
  return (
    <button
      type="button"
      className={`hr-pc-fav ${favorited ? 'is-active' : ''}`}
      onClick={handleToggle}
      aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
      aria-pressed={favorited}
    >
      <HeartIcon filled={favorited} />
    </button>
  )
}

/** Whether the card renders a media block, mirroring CardMedia's null-return. */
export function hasMedia(property: ExploreProperty, options: SharedCardOptions): boolean {
  return mediaLimit(options) > 0 && property.images.length > 0
}

/** Photo count the media component should render, honoring showImage (#742). */
export function mediaLimit(options: SharedCardOptions): number {
  return options.showImage === false ? 0 : options.maxImages
}
